// War replays: how long a replay takes, how its time is warped towards the fighting, and which chapter is showing.

import { CT_UMBRELLA, type Conflicts } from "./data";

/** Seconds a replay takes at 1×, chapter pauses aside: grows with the logarithm of the war's length — about half a
 * minute for a six-day war, a minute and a half for World War II, two minutes for the Hundred Years' War. */
export function replaySeconds(t0: number, t1: number): number {
  const days = Math.max(1, (t1 - t0) * 365.25);
  return Math.min(150, Math.max(25, 12 + 23.4 * Math.log10(days)));
}

/** Seconds a chapter pauses the replay: long enough to read its caption. */
export function chapterHold(text: string): number {
  return Math.min(6.5, Math.max(3, 1.4 + text.length / 24));
}

/** Replay progress u ∈ [0, 1] ↔ time t ∈ [a, b], piecewise linear between knots at equal time steps. */
export interface Warp { a: number; b: number; ts: Float64Array; us: Float64Array }

const SIGMA = 1.5; // smoothing of the pacing profile, in bins
const CAP = 6; // a slice of the war never gets more than 6× the average share of the fighting

/** Half of a replay's running time follows the calendar and half follows the fighting (where its battles and
 * incidents fall, smoothed), so quiet years pass quickly and decisive months get time. With almost no recorded
 * events the replay simply follows the calendar. */
export function buildWarp(a: number, b: number, pace: number[] | null): Warp {
  const bins = pace?.length || 1;
  const total = pace ? pace.reduce((x, y) => x + y, 0) : 0;
  const beta = total >= 20 ? 0.5 : total >= 6 ? (0.5 * (total - 6)) / 14 : 0;
  const dens = new Float64Array(bins).fill(1);
  if (beta > 0 && pace) {
    const sm = new Float64Array(bins);
    for (let i = 0; i < bins; i++) {
      let s = 0, w = 0;
      for (let k = -4; k <= 4; k++) {
        const j = i + k;
        if (j < 0 || j >= bins) continue;
        const g = Math.exp(-(k * k) / (2 * SIGMA * SIGMA));
        s += pace[j] * g;
        w += g;
      }
      sm[i] = s / w;
    }
    const mean = sm.reduce((x, y) => x + y, 0) / bins;
    let sum = 0;
    for (let i = 0; i < bins; i++) { sm[i] = Math.min(CAP, sm[i] / mean); sum += sm[i]; }
    for (let i = 0; i < bins; i++) dens[i] = 1 - beta + (beta * sm[i] * bins) / sum;
  }
  const ts = new Float64Array(bins + 1), us = new Float64Array(bins + 1);
  for (let i = 0; i <= bins; i++) {
    ts[i] = a + ((b - a) * i) / bins;
    us[i] = i === 0 ? 0 : us[i - 1] + dens[i - 1];
  }
  for (let i = 1; i <= bins; i++) us[i] /= us[bins];
  return { a, b, ts, us };
}

/** Time at replay progress u. */
export function timeAt(w: Warp, u: number): number {
  if (u <= 0) return w.a;
  if (u >= 1) return w.b;
  let lo = 0, hi = w.us.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (w.us[mid] <= u) lo = mid;
    else hi = mid;
  }
  const f = (u - w.us[lo]) / (w.us[hi] - w.us[lo] || 1);
  return w.ts[lo] + f * (w.ts[hi] - w.ts[lo]);
}

/** Replay progress at time t (inverse of timeAt). */
export function progressAt(w: Warp, t: number): number {
  if (t <= w.a) return 0;
  if (t >= w.b) return 1;
  const n = w.ts.length - 1;
  const x = ((t - w.a) / (w.b - w.a)) * n;
  const k = Math.min(n - 1, Math.floor(x));
  return w.us[k] + (x - k) * (w.us[k + 1] - w.us[k]);
}

/** Index of the last chapter at or before t (-1 before the first). */
export function chapterAt(chapters: { t: number }[], t: number): number {
  let k = -1;
  for (let i = 0; i < chapters.length && chapters[i].t <= t + 1e-7; i++) k = i;
  return k;
}

/** The conflicts a war consists of: itself, its theatres and campaigns at any depth and, for an umbrella such as
 * the Lithuanian Wars of Independence, its member wars. Their battles stay bright while it is selected. */
export function familyMask(c: Conflicts, i: number): Uint8Array {
  const m = new Uint8Array(c.n);
  for (let k = 0; k < c.n; k++) {
    for (let x = k, guard = 0; x >= 0 && guard < 64; guard++) {
      if (x === i) { m[k] = 1; break; }
      const g = c.g[x];
      x = c.p[x] >= 0 ? c.p[x] : g >= 0 && c.ct[g] === CT_UMBRELLA ? g : -1;
    }
  }
  return m;
}

/** Chapter views are written for a map about 1100 px wide; narrower maps zoom out to show the same area. */
export function zoomForWidth(zoom: number, width: number): number {
  return Math.max(0.6, zoom + Math.log2(Math.min(1, Math.max(width, 200) / 1100)));
}
